"use strict";
/**
 * Client-side entry point for ESI banner hydration
 *
 * ESI (Edge Side Includes) Context:
 * - Each ESI fragment contains its own script bundle
 * - Multiple banners can exist on the same page
 * - Each banner must hydrate independently
 * - Dynamic banners may be loaded after initial page render
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
// Capture the currently executing script immediately
// Must be at the top before any other code executes, as document.currentScript
// becomes null in async contexts
const currentScript = document.currentScript;
const react_1 = __importDefault(require("react"));
const client_1 = require("react-dom/client");
const constants_1 = require("../constants");
const KevelBannerPreviewer_1 = __importDefault(require("../components/KevelBannerPreviewer/KevelBannerPreviewer"));
const esiHydration_1 = require("../utils/esiHydration");
const domHelpers_1 = require("../utils/domHelpers");
/**
 * Hydrate the banner associated with the currently executing script.
 *
 * ESI Isolation Strategy:
 * Each ESI fragment includes its own script, so this code runs once per fragment.
 * We use document.currentScript to maintain the one-to-one relationship between
 * script and banner.
 */
const esiToHydrate = (0, domHelpers_1.findBannerToHydrate)(currentScript);
if (esiToHydrate) {
    (0, esiHydration_1.hydrateESIBanner)(esiToHydrate);
}
/**
 * Handle dynamically added ESI fragments (e.g., NavFlyout loaded on-demand).
 *
 * MutationObserver watches for:
 * - New banner elements added to the DOM
 * - Banner elements within newly added containers
 */
const handleDynamicBanners = (mutations) => {
    mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node;
                const bannersToHydrate = (0, domHelpers_1.findUnhydratedBannersIn)(element);
                bannersToHydrate.forEach((banner) => {
                    (0, esiHydration_1.hydrateESIBanner)(banner);
                });
            }
        });
    });
};
// Watch for dynamically added ESI fragments
const observer = new MutationObserver(handleDynamicBanners);
// Start observing the document for dynamically added ESI fragments
observer.observe(document.body, {
    childList: true,
    subtree: true,
});
/**
 * Handle banner previewer separately (single instance, not ESI).
 *
 * The previewer is a standalone tool for viewing banners in isolation,
 * so it has its own hydration logic separate from the ESI banner system.
 */
const hydratePreviewerIfPresent = () => {
    const previewerDataElem = document.getElementById(constants_1.BANNER_PREVIEWER_DATA_ELEM_ID);
    if (!previewerDataElem || !previewerDataElem.textContent) {
        return;
    }
    const previewerRootElem = document.getElementById(constants_1.BANNER_PREVIEWER_ROOT_ELEM_ID);
    if (!previewerRootElem) {
        return;
    }
    try {
        const data = JSON.parse(previewerDataElem.textContent);
        (0, client_1.hydrateRoot)(previewerRootElem, react_1.default.createElement(KevelBannerPreviewer_1.default, Object.assign({}, data)));
    }
    catch (error) {
        console.error("Failed to hydrate banner previewer:", error);
    }
};
hydratePreviewerIfPresent();
